import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { isInside } from '../../../../utils';

import './Pagination.scss';

/*
Constants and helpers
*/

const DEFAULT_PAGE_NUMBER = '1';

// Values are strings, since native select always returns String as 'value'
const DEFAULT_PAGE_SIZE_OPTIONS = [
  { value: '25', label: '25 rows' },
  { value: '50', label: '50 rows' },
  { value: '100', label: '100 rows' },
  { value: '200', label: '200 rows' },
];

const DEFAULT_PAGE_SIZE_VALUE = '25';


// Milliseconds to wait after a click on "Next" or "Previous" before submitting
const MULTI_CLICK_TIMEOUT = 500;

const PAGE_NUMBER_REGEX = /^$|^[1-9]+[0-9]*$/;

// Calculates the new current page number and new total number of pages
// from current page size, new page size, and the total number for rows.
// All arguments are expected to be Number or number String.
// Returns object { newPageNum, newTotalPages } with fields of type Number.
const getPageNum = ({
  pageNum,
  pageSize,
  newPageSize,
  totalItems,
}) => {
  const newTotalPages = Number(totalItems) / Number(newPageSize);

  // First row on the current page
  const firstRow = (Number(pageNum) - 1) * Number(pageSize) + 1;

  // New page that contains 'firstRow'
  const newPageNum = Math.ceil(firstRow / Number(newPageSize));

  return {
    newPageNum,
    newTotalPages: Math.ceil(newTotalPages),
  };
};

// Returns the options list for the page size select
const getSelectOptions = sizeOptions => sizeOptions
  .map(({ label, value }, index) => (
    <option value={value} key={index}>
      {label}
    </option>
  ));

// Validates user's page number input.
// Takes both, string and number, parameter values.
const validatePageNum = ({ totalPages, value }) => {
  // The input must be a positive integer
  const found = String(value).match(PAGE_NUMBER_REGEX);

  if (found === null) {
    return false;
  }

  // The input cannot exceed the total number of pages
  if (Number(value) > Number(totalPages)) {
    return false;
  }

  // The checks are complete
  return true;
};

/*
Component
*/

// Expects pageNum and pageSize properties to be of type Number
const Pagination = (props) => {
  const {
    onChange,
    pageNum,
    pageSize,
    sizeOptions,
    totalItems,
    totalPages,
  } = props;

  // currentNum, currentSize, currentTotalPages are of type String
  const [currentNum, setCurrentNum] = useState(DEFAULT_PAGE_NUMBER);
  const [currentSize, setCurrentSize] = useState(DEFAULT_PAGE_SIZE_VALUE);
  const [currentTotalPages, setCurrentTotalPages] = useState('');

  const [numTouched, setNumTouched] = useState(false);

  // "Next" and "Previous" submit timeout handle
  const [timeoutHandle, setTimeoutHandle] = useState(null);

  // Used to check if the click is outside the page size input box
  const pageNumElt = useRef();

  useEffect(() => {
    setCurrentNum(String(pageNum));
  }, [pageNum]);

  useEffect(() => {
    setCurrentSize(String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    setCurrentTotalPages(String(totalPages));
  }, [totalPages]);

  // Wrapper for the onChange function
  const update = ({ page, size }) => {
    onChange({ pageNum: Number(page), pageSize: Number(size) });
  };

  // A wrapper for 'update' for updating the page number.
  // Calls 'update' iff the currentNum is different from pageNum
  // and is non-empty
  const updatePageNum = () => {
    // If untouched - return
    if (!numTouched) {
      return;
    }

    // If the page number has not changed - return
    if (currentNum === String(pageNum)) {
      return;
    }

    setNumTouched(false);

    // If no input - reset input to pageNum
    if (currentNum === '') {
      setCurrentNum(String(pageNum));
      return;
    }

    // If there is input - update page number
    update({ page: currentNum, size: currentSize });
  };

  // Submit page number on outside click
  useEffect(() => {
    const onOutsideClick = (evt) => {
      const inside = isInside(evt, pageNumElt.current);

      // If inside page number input - return
      if (inside) {
        return;
      }

      updatePageNum();
    };

    window.addEventListener('click', onOutsideClick, false);

    return () => window.removeEventListener('click', onOutsideClick, false);
  });

  // Restore on 'Escape', submit on "Enter",
  const onKeyDownNum = (evt) => {
    const { key } = evt;

    // If key is 'Escape', reset currentNum to pageNum
    if (key === 'Escape') {
      evt.target.blur();

      setCurrentNum(String(pageNum));

      return;
    }

    // If key is 'Enter', update pageNum to currentNum
    if (key === 'Enter') {
      evt.target.blur();

      updatePageNum();
    }
  };

  // Page number onChange handler
  const onChangeNum = (evt) => {
    const { target = {} } = evt;
    const { value } = target;

    const validated = validatePageNum({ totalPages, value });

    if (!validated) {
      return;
    }

    setNumTouched(true);
    setCurrentNum(value);
  };

  // Page size onChange handler
  const onChangeSize = (evt) => {
    const { target = {} } = evt;
    const { value } = target;

    // Calculate the new page number and the new total number of pages
    // using the new page size and the total number of rows.
    const {
      newPageNum,
      newTotalPages,
    } = getPageNum({
      pageNum,
      pageSize,
      newPageSize: value,
      totalItems,
    });

    // Set the new page size, page number, total pages
    setCurrentSize(value);
    setCurrentNum(String(newPageNum));
    setCurrentTotalPages(String(newTotalPages));

    update({ page: newPageNum, size: value });
  };

  // onClick hadler for 'Previous' and 'Next'
  const onClickNext = (delta = 1) => (evt) => {
    // evt.preventDefault();
    evt.stopPropagation();

    const newPageNum = Number(currentNum || pageNum) + delta;

    const validated = validatePageNum({ totalPages, value: newPageNum });
    if (!validated) {
      return;
    }

    setCurrentNum(String(newPageNum));

    // Clear existing timeout
    if (timeoutHandle) {
      window.clearTimeout(timeoutHandle);
    }

    // Set new timeout
    const handle = window.setTimeout(() => {
      update({ page: newPageNum, size: pageSize });
    }, MULTI_CLICK_TIMEOUT);

    // Save the handle
    setTimeoutHandle(handle);
  };

  const disabledNext = Number(currentNum) >= Number(currentTotalPages);
  const disabledPrevious = Number(currentNum) <= 1;
  const sizeSelectOptions = getSelectOptions(sizeOptions);
  const totalItemsStr = `of ${(totalItems || '').toLocaleString()}`;

  return (
    <div className="blu-pagination">
      <button
        disabled={disabledPrevious}
        onClick={onClickNext(-1)}
        type="button"
      >
        Previous
      </button>

      <div className="blu-pagination-center">
        <span className="wording">
          Page
        </span>

        <input
          ref={pageNumElt}
          aria-label="jump to page"
          onChange={onChangeNum}
          onKeyDown={onKeyDownNum}
          value={currentNum}
        />

        <span className="wording">of</span>

        <span>{currentTotalPages || ''}</span>

        <select
          onChange={onChangeSize}
          value={currentSize}
        >
          {sizeSelectOptions}
        </select>

        <span className="wording">
          {totalItemsStr}
        </span>
      </div>

      <button
        disabled={disabledNext}
        onClick={onClickNext()}
        type="button"
      >
        Next
      </button>
    </div>
  );
};

Pagination.propTypes = {
  onChange: PropTypes.func,
  pageNum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pageSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sizeOptions: PropTypes.arrayOf(PropTypes.shape({})),
  totalPages: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  totalItems: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Pagination.defaultProps = {
  onChange: () => {},
  pageNum: 1,
  pageSize: DEFAULT_PAGE_SIZE_VALUE,
  sizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
  totalPages: 1,
  totalItems: 0,
};

export default Pagination;
